import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, Tag, ArrowLeft, Newspaper, Share2, Link2, Copy, MessageCircle, Send, Trash2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { BACKEND_URL, useAuth } from "../context/AuthContext";

export default function NewsPostPage() {
  const { slug } = useParams();
  const { user, token } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  const [copied, setCopied] = useState(false);
  const postUrl = typeof window !== 'undefined' ? `${window.location.origin}/news/${slug}` : '';

  const handleShare = (platform) => {
    const text = post ? encodeURIComponent(post.title) : '';
    const ogUrl = `${BACKEND_URL}/api/og/news/${slug}`;
    const directUrl = postUrl;
    const links = {
      twitter: `https://x.com/intent/tweet?text=${text}&url=${encodeURIComponent(ogUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogUrl)}`,
    };
    if (platform === 'copy' || (platform === 'native' && !navigator.share)) {
      navigator.clipboard.writeText(directUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else if (platform === 'native') {
      navigator.share({ title: post.title, url: directUrl });
    } else {
      window.open(links[platform], '_blank', 'width=600,height=400');
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${BACKEND_URL}/api/news/${slug}`, { headers })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setPost)
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
    fetchComments();
  }, [slug]);

  const fetchComments = () => {
    fetch(`${BACKEND_URL}/api/news/${slug}/comments`)
      .then(r => r.json())
      .then(data => setComments(Array.isArray(data) ? data : []))
      .catch(() => setComments([]));
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !token) return;
    setCommentSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${slug}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text: commentText.trim() }),
      });
      if (res.ok) { setCommentText(''); fetchComments(); }
    } catch (err) { console.error(err); }
    finally { setCommentSubmitting(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await fetch(`${BACKEND_URL}/api/news/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      fetchComments();
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0a0a14] text-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <Newspaper className="w-16 h-16 text-white/10 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Article Not Found</h1>
          <Link to="/news" className="text-purple-400 hover:text-purple-300 text-sm">Back to News</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white">
      <SEO
        title={post.title}
        description={post.excerpt || post.body?.slice(0, 160)}
        image={post.featured_image || "/og-image.png"}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "author": { "@type": "Person", "name": post.author },
          "datePublished": post.created_at,
          "dateModified": post.updated_at,
          "image": post.featured_image,
          "publisher": { "@type": "Organization", "name": "DJsandMCMedia" },
        }}
      />
      <Header />

      <main className="max-w-3xl mx-auto px-6 py-20">
        <Link to="/news" className="inline-flex items-center gap-1.5 text-purple-400 hover:text-purple-300 text-sm mb-8 transition block" data-testid="news-back">
          <ArrowLeft className="w-4 h-4" /> Back to News
        </Link>

        {/* Category & Meta */}
        <div className="mb-2">
          <span className="text-purple-400 text-xs font-semibold uppercase tracking-wider">{post.category}</span>
        </div>
        <h1 data-testid="news-post-title" className="text-3xl sm:text-4xl font-bold mt-2 mb-4">{post.title}</h1>
        <div className="flex items-center gap-4 text-white/40 text-sm mb-4">
          <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{post.author}</span>
          <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>

        {/* Share Bar */}
        <div className="flex items-center gap-2 mb-8" data-testid="news-post-share">
          <span className="text-white/30 text-sm mr-1">Share:</span>
          <button onClick={() => handleShare('twitter')} className="p-2 rounded-lg bg-white/5 hover:bg-[#1da1f2]/20 text-white/40 hover:text-[#1da1f2] transition" data-testid="share-twitter" title="Share on X">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </button>
          <button onClick={() => handleShare('facebook')} className="p-2 rounded-lg bg-white/5 hover:bg-[#1877f2]/20 text-white/40 hover:text-[#1877f2] transition" data-testid="share-facebook" title="Share on Facebook">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </button>
          <button onClick={() => handleShare('copy')} className={`p-2 rounded-lg bg-white/5 text-white/40 transition ${copied ? 'bg-green-500/20 text-green-400' : 'hover:bg-white/10 hover:text-white'}`} data-testid="share-copy" title="Copy link">
            {copied ? <Copy className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
          </button>
          <button onClick={() => handleShare('native')} className="p-2 rounded-lg bg-white/5 hover:bg-purple-500/20 text-white/40 hover:text-purple-400 transition" data-testid="share-native" title="More sharing options">
            <Share2 className="w-4 h-4" />
          </button>
        </div>

        {/* Featured Image */}
        {post.featured_image && (
          <div className="rounded-2xl overflow-hidden mb-10 border border-white/[0.08]">
            <img src={post.featured_image} alt={post.title} className="w-full object-cover" />
          </div>
        )}

        {/* Body */}
        <article
          data-testid="news-post-body"
          className="prose prose-invert prose-purple max-w-none
            prose-headings:text-white prose-headings:font-bold
            prose-p:text-white/70 prose-p:leading-relaxed
            prose-a:text-purple-400 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white prose-li:text-white/70
            prose-blockquote:border-purple-500 prose-blockquote:text-white/50
            prose-img:rounded-xl"
          dangerouslySetInnerHTML={{ __html: post.body.replace(/\n/g, '<br/>') }}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-white/[0.08]">
            <Tag className="w-4 h-4 text-white/30" />
            {post.tags.map((tag, i) => (
              <span key={i} className="bg-white/5 text-white/50 px-3 py-1 rounded-full text-xs">{tag}</span>
            ))}
          </div>
        )}

        {/* Comments Section */}
        <div className="mt-12 pt-8 border-t border-white/[0.08]" data-testid="news-comments-section">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-purple-400" />
            Comments {comments.length > 0 && <span className="text-sm text-white/40">({comments.length})</span>}
          </h3>

          {/* Comment Input */}
          {user ? (
            <div className="mb-8 flex gap-3" data-testid="comment-form">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 text-purple-400 font-bold text-sm">
                {(user.name || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Share your thoughts..."
                  maxLength={1000}
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition resize-none text-sm"
                  data-testid="comment-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-white/20 text-xs">{commentText.length}/1000</span>
                  <button
                    onClick={handleSubmitComment}
                    disabled={commentSubmitting || !commentText.trim()}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-40 text-white text-sm rounded-lg font-medium transition flex items-center gap-1.5"
                    data-testid="comment-submit"
                  >
                    <Send className="w-3.5 h-3.5" /> {commentSubmitting ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-8 p-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-center">
              <p className="text-white/40 text-sm">
                <Link to="/" className="text-purple-400 hover:text-purple-300 transition">Sign in</Link> to join the conversation.
              </p>
            </div>
          )}

          {/* Comments List */}
          {comments.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-10 h-10 text-white/10 mx-auto mb-2" />
              <p className="text-white/30 text-sm">No comments yet. Be the first to share your thoughts!</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="comments-list">
              {comments.map((c) => (
                <div key={c.comment_id} className="flex gap-3 group" data-testid={`comment-${c.comment_id}`}>
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-white/50 font-bold text-xs">
                    {(c.user_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-medium text-sm">{c.user_name}</span>
                      <span className="text-white/20 text-xs">{new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      {(user?.is_admin || user?.user_id === c.user_id) && (
                        <button onClick={() => handleDeleteComment(c.comment_id)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-white/20 hover:text-red-400 transition"
                          data-testid={`comment-delete-${c.comment_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <p className="text-white/60 text-sm leading-relaxed">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
